import _ from 'underscore';

import {ClientSchemaField, createClientSchemaField} from 'registry/field/FieldTable';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';

import {
  MOLECULE_LOT_DISPLAY_NAME,
  MOLECULE_LOT_FIELD_NAMES,
  MOLECULE_LOT_SCHEMA_INTERFACE_ID,
} from './MoleculeLotConstants';

/**
 * Minimal shape needed for the target molecule schema when creating the parent link field.
 * Compatible with TagSchemaSummary / TagSchemaSummaryGraphQL's required subset for RequiredLink.
 */
export type MoleculeSchemaSummary = SchemaFieldConstants.RequiredLinkTagSchema['tagSchema'];

const MOLECULE_LOT_SYSTEM_NAMES: Set<string> = new Set(
  Object.values(MOLECULE_LOT_FIELD_NAMES).map((f) => f.systemName)
);

function createMoleculeLotField(
  displayName: string,
  systemName: string,
  fieldType: SchemaFieldConstants.FieldType,
  overrides?: Partial<ClientSchemaField>
): ClientSchemaField {
  const base = createClientSchemaField(displayName, systemName);
  return {
    ...base,
    cid: _.uniqueId(),
    fieldType,
    appliedFieldset: {
      id: MOLECULE_LOT_SCHEMA_INTERFACE_ID,
      name: MOLECULE_LOT_DISPLAY_NAME,
    },
    ...overrides,
  };
}

function getMoleculeLotEnforcedFields(targetMoleculeSchema: MoleculeSchemaSummary): Array<ClientSchemaField> {
  const {MOLECULE_LINK, PURITY, CONCENTRATION_MOLAR, CONCENTRATION_DENSITY} = MOLECULE_LOT_FIELD_NAMES;
  return [
    createMoleculeLotField(
      MOLECULE_LINK.displayName,
      MOLECULE_LINK.systemName,
      SchemaFieldConstants.FieldType.BIOENTITY_LINK,
      {
        isRequired: true,
        isParentLink: true,
        // No appliedFieldset — the molecule link field remains editable so the user
        // can change which molecule schema it links to via the dropdown.
        appliedFieldset: null,
        requiredLink: {
          tagSchema: targetMoleculeSchema,
          entitySchemaFieldPath: null,
          folderItemType: null,
          schemaInterface: null,
        },
      }
    ),
    createMoleculeLotField(PURITY.displayName, PURITY.systemName, SchemaFieldConstants.FieldType.FLOAT, {
      appliedFieldset: null,
      simpleRules: [
        {
          validationRuleTemplateId: 'svdrltmpl_floatfieldminvalue',
          constantInputs: {float_min_value: 0},
        },
        {
          validationRuleTemplateId: 'svdrltmpl_floatfieldmaxvalue',
          constantInputs: {float_max_value: 100},
        },
      ],
    }),
    createMoleculeLotField(
      CONCENTRATION_MOLAR.displayName,
      CONCENTRATION_MOLAR.systemName,
      SchemaFieldConstants.FieldType.FLOAT,
      {appliedFieldset: null}
    ),
    createMoleculeLotField(
      CONCENTRATION_DENSITY.displayName,
      CONCENTRATION_DENSITY.systemName,
      SchemaFieldConstants.FieldType.FLOAT,
      {appliedFieldset: null}
    ),
  ];
}

export function removeMoleculeLotEnforcedFields(fields: Array<ClientSchemaField>): Array<ClientSchemaField> {
  return fields.filter(
    (field) =>
      field.appliedFieldset?.id !== MOLECULE_LOT_SCHEMA_INTERFACE_ID &&
      !MOLECULE_LOT_SYSTEM_NAMES.has(field.systemName)
  );
}

export function computeUpdatedFieldsForMoleculeLot(
  checked: boolean,
  existingFields: Array<ClientSchemaField>,
  targetMoleculeSchema?: MoleculeSchemaSummary
): Array<ClientSchemaField> {
  const fieldsWithoutMoleculeLot = removeMoleculeLotEnforcedFields(existingFields);

  if (!checked) {
    return fieldsWithoutMoleculeLot;
  }

  if (!targetMoleculeSchema) {
    // Caller should prevent this — shows an error notification via notifications.error()
    return fieldsWithoutMoleculeLot;
  }

  return [...getMoleculeLotEnforcedFields(targetMoleculeSchema), ...fieldsWithoutMoleculeLot];
}
